import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import YouTube from 'react-youtube';
import { getSubjects, getChapters, getVideos } from '../../api';

function getVideoId(url) {
  try {
    const u = new URL(url);
    return u.searchParams.get('v') || u.pathname.split('/').pop();
  } catch { return url; }
}

function ChapterVideos({ chapter }) {
  const [activeVideo, setActiveVideo] = useState(null);
  const { data: videos = [] } = useQuery({
    queryKey: ['videos', chapter.id],
    queryFn: () => getVideos(chapter.id).then(r => r.data),
  });
  if (videos.length === 0) return null;
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 10, padding: '6px 12px', background: 'var(--bg)', borderRadius: 8, borderLeft: '4px solid var(--primary)', color: 'var(--text)' }}>
        {chapter.name} <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 }}>({videos.length} video{videos.length !== 1 ? 's' : ''})</span>
      </h3>
      {activeVideo?.id === chapter.id && activeVideo.video && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ borderRadius: 10, overflow: 'hidden', background: '#000', marginBottom: 10 }}>
            <YouTube videoId={getVideoId(activeVideo.video.youtube_url)} opts={{ width: '100%', height: '380', playerVars: { autoplay: 1 } }} style={{ display: 'block' }} />
          </div>
          <div style={{ fontWeight: 600, fontSize: 15 }}>{activeVideo.video.title}</div>
          {activeVideo.video.description && <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>{activeVideo.video.description}</p>}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {videos.map((v, i) => {
          const isActive = activeVideo?.id === chapter.id && activeVideo?.video?.id === v.id;
          return (
            <div key={v.id} onClick={() => setActiveVideo(isActive ? null : { id: chapter.id, video: v })}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 10, cursor: 'pointer', transition: 'all 0.15s', border: `1.5px solid ${isActive ? 'var(--primary)' : 'var(--border)'}`, background: isActive ? 'var(--primary-light)' : 'white' }}>
              <div style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, background: isActive ? 'var(--primary)' : 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>
                {isActive ? 'P' : 'V'}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 14, color: isActive ? 'var(--primary-dark)' : 'var(--text)' }}>{i + 1}. {v.title}</div>
                {v.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.description}</div>}
              </div>
              {isActive && <span style={{ fontSize: 12, color: 'var(--primary)', fontWeight: 600 }}>Playing</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SubjectSection({ subject }) {
  const { data: chapters = [], isLoading } = useQuery({
    queryKey: ['chapters', subject.id],
    queryFn: () => getChapters(subject.id).then(r => r.data),
  });
  if (isLoading) return null;
  return (
    <div style={{ marginBottom: 40 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, paddingBottom: 12, borderBottom: '2px solid var(--border)' }}>
        <h2 style={{ fontSize: 20, fontFamily: 'Sora' }}>{subject.name}</h2>
      </div>
      {chapters.map(chapter => <ChapterVideos key={chapter.id} chapter={chapter} />)}
    </div>
  );
}

export default function VideosPage() {
  const { data: subjects = [], isLoading } = useQuery({
    queryKey: ['subjects'],
    queryFn: () => getSubjects().then(r => r.data),
  });
  return (
    <div>
      <h1 style={{ fontSize: 26, marginBottom: 4 }}>Video Lessons</h1>
      <p style={{ color: 'var(--text-muted)', marginBottom: 32, fontSize: 14 }}>Browse all videos by subject and chapter</p>
      {isLoading ? <div className="spinner" /> : subjects.map(s => <SubjectSection key={s.id} subject={s} />)}
    </div>
  );
}
